import {
  AHText,
  Box,
  StoreFormattedDate,
  TouchableBox,
  Image,
  SubjectBreadcrumbs,
  Card,
  AHTextFonts,
} from "arkh";
import {
  useLazyLoadQuery,
  graphql,
  usePaginationFragment,
  useFragment,
} from "react-relay";

import { ActivityIndicator, FlatList } from "react-native";
import React, {
  useCallback,
  useDeferredValue,
  useState,
  useTransition,
} from "react";

import { useWorkspaceId } from "../workspaceId";

import { LazyQueryNetworkBoundrary } from "@/src/components/mobile/QueryNetworkBoundrary";
import { useOrgListStyleProps } from "../useOrgHomeDetailContainerStyle";
import { WorkspaceDataTodoMainQuery } from "./__generated__/WorkspaceDataTodoMainQuery.graphql";

import { WorkspaceDataTodoGallery_Workspace$key } from "./__generated__/WorkspaceDataTodoGallery_Workspace.graphql";
import { useNavigation } from "@react-navigation/native";
import { OrgSearchInput } from "../OrgSearchInput";
import { WorkspaceDataTodoGalleryItem_todo$key } from "./__generated__/WorkspaceDataTodoGalleryItem_todo.graphql";
import { OrgButton } from "../OrgButton";

export function WorkspaceDataTodoMainScreen() {
  const wId = useWorkspaceId();

  return (
    <LazyQueryNetworkBoundrary>
      {(fk) => (
        <WorkspaceDataTodoScreenComponent workspaceId={wId} fetchKey={fk} />
      )}
    </LazyQueryNetworkBoundrary>
  );
}

function WorkspaceDataTodoScreenComponent(props: {
  workspaceId: string;
  fetchKey: string;
}) {
  const [isLoading, startTransition] = useTransition();
  const [search, setSearch] = useState("");
  const [issueClassFilter, setIssueClassFilter] = useState<string | null>(null);
  const defferedIssueClassFilter = useDeferredValue(issueClassFilter);

  const data = useLazyLoadQuery<WorkspaceDataTodoMainQuery>(
    graphql`
      query WorkspaceDataTodoMainQuery(
        $workspaceId: ID!
        $first: Int
        $after: String
        $query: String
        $issueClassFilter: String
      ) {
        workspace(id: $workspaceId) @required(action: THROW) {
          ...WorkspaceDataTodoGallery_Workspace
        }
      }
    `,
    {
      workspaceId: props.workspaceId,
      first: 40,
      query: search,
      issueClassFilter: defferedIssueClassFilter,
    },
    {
      fetchKey: props.fetchKey,
      fetchPolicy: "store-and-network",
    }
  );

  return (
    <Box spaceVertical="large" style={{ flex: 1 }}>
      <Gallery
        isLoading={isLoading}
        workspace={data.workspace}
        search={search}
        issueClassFilter={issueClassFilter}
        setIssueClassFilter={(issueClassFilter) => {
          setIssueClassFilter((prev) =>
            prev === issueClassFilter ? null : issueClassFilter
          );
        }}
        setSearch={(query) => {
          startTransition(() => {
            setSearch(query);
          });
        }}
      />
    </Box>
  );
}

function Gallery(props: {
  workspace: WorkspaceDataTodoGallery_Workspace$key;
  search: string;
  isLoading: boolean;
  setSearch: (search: string) => void;
  setIssueClassFilter: (issueClassFilter: string) => void;
  issueClassFilter: string | null;
}) {
  const nav = useNavigation<any>();

  const openTodo = useCallback(
    (todoId: string, storeName: string, timezone: string) => {
      nav.navigateDeprecated("TodoEdit", {
        todoId,
        storeName,
        storeTimezone: timezone,
      });
    },
    [nav]
  );

  const { data, hasNext, loadNext, isLoadingNext } = usePaginationFragment(
    graphql`
      fragment WorkspaceDataTodoGallery_Workspace on Workspace
      @refetchable(
        queryName: "WorkspaceDataTodoGallery_Workspace_WorkspaceRefetchQuery"
      ) {
        id
        gallery: storeTodos(
          first: $first
          after: $after
          filter: { requireImages: true, requireTaskExecution: true }
          query: $query
          issueClass: $issueClassFilter
        ) @connection(key: "WorkspaceDataTodoGallery_Workspace__gallery") {
          matchingIssueClasses {
            label
            count
          }
          edges {
            score
            node {
              id
              title

              store {
                name
                timezone
                id
              }
              ...WorkspaceDataTodoGalleryItem_todo
            }
          }
        }
      }
    `,
    props.workspace
  );

  const detailLayout = useOrgListStyleProps();

  const edges = data.gallery.edges;
  const onEndReached = useCallback(() => {
    if (hasNext && !isLoadingNext) {
      loadNext(40);
    }
  }, [hasNext, isLoadingNext, loadNext]);

  const [searchText, setSearchText] = useState(props.search);

  function onSubmit() {
    props.setSearch(searchText);
  }
  function onCancelSearch() {
    props.setSearch("");
  }

  return (
    <Box style={{ flex: 1, flexDirection: "row" }}>
      <Box spaceLeft="medium">
        <Box style={{ width: 200 }}>
          <Card defaultBorder>
            <AHText type="subtitle">Issue Types</AHText>
            <Box spaceVertical="small" gap="small">
              {data.gallery.matchingIssueClasses.map((ic) => {
                const isSelected = ic.label === props.issueClassFilter;
                return (
                  <TouchableBox
                    key={ic.label}
                    cornerRadius="medium"
                    spaceVertical={"xsmall"}
                    color={isSelected ? "primary" : "surface-background-light"}
                    spaceHorizontal="small"
                    borderColor={isSelected ? "primary" : "border-primary"}
                    onPress={() => {
                      props.setIssueClassFilter(ic.label);
                    }}
                    style={{
                      borderWidth: 1,
                      flexShrink: 1,
                      flexDirection: "row",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <AHText
                      type={"body"}
                      ellipsizeMode="tail"
                      numberOfLines={1}
                      color={isSelected ? "lightText" : "darkerText"}
                      style={{
                        flexShrink: 1,

                        fontFamily: AHTextFonts.BOLD,
                      }}
                    >
                      {ic.label}
                    </AHText>
                    <AHText
                      type="detail"
                      color={isSelected ? "lightText" : "darkerText"}
                    >
                      {ic.count}
                    </AHText>
                  </TouchableBox>
                );
              })}
            </Box>
          </Card>
        </Box>
      </Box>
      <FlatList
        {...detailLayout}
        style={{ flex: 1 }}
        contentContainerStyle={[
          detailLayout.contentContainerStyle,
          {
            minWidth: 450,
          },
        ]}
        data={edges}
        onEndReached={onEndReached}
        numColumns={3}
        ListHeaderComponent={
          <Box spaceHorizontal="medium">
            <Box style={{ flexDirection: "row", alignItems: "center" }}>
              <Box style={{ flex: 1 }} spaceRight="medium">
                <OrgSearchInput
                  isLoading={props.isLoading}
                  onChangeText={setSearchText}
                  value={searchText}
                  placeholder="Search"
                  mode="standalone"
                  onSubmitEditing={onSubmit}
                  onCancelSearch={onCancelSearch}
                />
              </Box>
              <OrgButton
                backgroundColor="darkerText"
                title="Search"
                tintColor="lightText"
                onPress={onSubmit}
              />
            </Box>
          </Box>
        }
        ListFooterComponent={
          isLoadingNext ? (
            <Box spaceVertical="large">
              <ActivityIndicator />
            </Box>
          ) : null
        }
        renderItem={({ item, index }) => {
          return (
            <TouchableBox
              style={{ width: "33%" }}
              onPress={() => {
                if (item.node.store) {
                  openTodo(
                    item.node.id,
                    item.node.store.name,
                    item.node.store.timezone
                  );
                }
              }}
            >
              <GalleryTodoItem todo={item.node} />
            </TouchableBox>
          );
        }}
      />
    </Box>
  );
}

function GalleryTodoItem(props: {
  todo: WorkspaceDataTodoGalleryItem_todo$key;
}) {
  const item = useFragment(
    graphql`
      fragment WorkspaceDataTodoGalleryItem_todo on Todo {
        subject {
          breadcrumbs(style: FULL)
        }
        createdAtDate {
          ...StoreFormattedDate
        }

        store {
          name
        }
        title
        images {
          id

          ...Image_image
        }
      }
    `,
    props.todo
  );
  let breadcrumbs = item.subject?.breadcrumbs.slice() ?? [];
  if (item.title) {
    breadcrumbs.push(item.title);
  }
  return (
    <Box space="medium">
      <Box
        style={{ height: 300, overflow: "hidden" }}
        color="surface"
        cornerRadius="small"
      >
        <Image
          contentFit="cover"
          image={item.images[0]}
          type="remote"
          style={{ flex: 1 }}
        />
      </Box>
      <Box
        style={{
          flexDirection: "row",
          flex: 1,
          justifyContent: "space-between",
        }}
      >
        <AHText
          numberOfLines={2}
          spaceTop="xsmall"
          type="detail"
          color="darkText"
        >
          {item.store?.name}
        </AHText>
        <AHText
          numberOfLines={2}
          spaceTop="xsmall"
          type="detail"
          color="darkText"
        >
          <StoreFormattedDate date={item.createdAtDate} />
        </AHText>
      </Box>
      <Box
        style={{
          flexDirection: "row",
          flex: 1,
          justifyContent: "space-between",
        }}
        spaceTop="xsmall"
      >
        <SubjectBreadcrumbs
          breadcrumbs={breadcrumbs}
          highlightLast
          placeholder="Issue"
        />
      </Box>
    </Box>
  );
}
